import React, { useState, useEffect } from 'react';
import { MessageCircle, MessageSquare, X, Sparkles, MessageCircleHeart } from 'lucide-react';
import AIChat from './AIChat';
import FeedbackForm from './FeedbackForm';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

// --- COMPONENTS ---

const NotificationRing = () => (
    <div className="absolute top-0 right-0 -mt-1 -mr-1">
        <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
        </span>
    </div>
);

interface AssistantButtonProps {
    isActive: boolean;
    onClick: () => void;
    icon: any;
    label: string;
    notification?: boolean;
    colorClass?: string;
    delay?: number;
}

const AssistantButton: React.FC<AssistantButtonProps> = ({
    isActive,
    onClick,
    icon: Icon,
    label,
    notification,
    colorClass = "text-white",
    delay = 0
}) => {
    const [isHovered, setIsHovered] = useState(false);

    // Auto-collapse logic when not active or hovered
    const isExpanded = isHovered || isActive;

    return (
        <div
            className="relative flex items-center justify-end"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        initial={{ opacity: 0, x: 20, scale: 0.8 }}
                        animate={{ opacity: 1, x: 0, scale: 1 }}
                        exit={{ opacity: 0, x: 10, scale: 0.8 }}
                        className="mr-3 bg-black/80 backdrop-blur-md px-3 py-1.5 rounded-lg border border-white/10 shadow-xl"
                    >
                        <span className="text-xs font-medium text-white whitespace-nowrap">{label}</span>
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.button
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: delay * 0.1, type: "spring", stiffness: 260, damping: 20 }}
                onClick={onClick}
                className={cn(
                    "relative group flex items-center justify-center rounded-full transition-all duration-300 shadow-2xl backdrop-blur-xl border border-white/10",
                    isActive
                        ? "w-12 h-12 bg-white text-black"
                        : "w-10 h-10 bg-black/60 hover:bg-black/80 hover:scale-110"
                )}
            >
                <div className={cn("transition-transform duration-300", isActive && "rotate-180")}>
                    {isActive ? (
                        <X size={20} />
                    ) : (
                        <Icon size={20} className={colorClass} />
                    )}
                </div>

                {!isActive && notification && <NotificationRing />}

                {/* Glow Effect */}
                {isActive && (
                    <div className="absolute inset-0 rounded-full bg-white/50 blur-lg -z-10 animate-pulse" />
                )}
            </motion.button>
        </div>
    );
};

export const FloatingAssistant: React.FC = () => {
    const [activeModal, setActiveModal] = useState<'chat' | 'feedback' | null>(null);
    const [notification, setNotification] = useState<'chat' | 'feedback' | null>(null);

    // Sequential notification logic
    useEffect(() => {
        const chatTimer = setTimeout(() => setNotification('chat'), 3000);
        const feedbackTimer = setTimeout(() => setNotification('feedback'), 15000);
        const clearTimer = setTimeout(() => setNotification(null), 25000);

        return () => {
            clearTimeout(chatTimer);
            clearTimeout(feedbackTimer);
            clearTimeout(clearTimer);
        };
    }, []);

    const handleToggle = (modal: 'chat' | 'feedback') => {
        if (activeModal === modal) {
            setActiveModal(null);
        } else {
            setActiveModal(modal);
            setNotification(null);
        }
    };

    return (
        <>
            <div className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-50 flex flex-col items-end gap-4 pointer-events-none">
                <div className="pointer-events-auto flex flex-col gap-3">

                    {/* Feedback Toggle */}
                    <AssistantButton
                        isActive={activeModal === 'feedback'}
                        onClick={() => handleToggle('feedback')}
                        icon={MessageSquare}
                        label="Send Feedback"
                        notification={notification === 'feedback'}
                        colorClass="text-blue-400"
                        delay={1}
                    />

                    {/* Chat Toggle (Primary) */}
                    <AssistantButton
                        isActive={activeModal === 'chat'}
                        onClick={() => handleToggle('chat')}
                        icon={Sparkles}
                        label="Ask AI Assistant"
                        notification={notification === 'chat'}
                        colorClass="text-purple-400"
                        delay={0}
                    />

                </div>
            </div>

            {/* Modals */}
            <AIChat
                isOpen={activeModal === 'chat'}
                onClose={() => setActiveModal(null)}
            />
            <FeedbackForm
                isOpen={activeModal === 'feedback'}
                onClose={() => setActiveModal(null)}
            />
        </>
    );
};
